import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";

const authSlice = createSlice({
  name: "auth",
  initialState: {
    status: "idle",
    error: null,
    loading: false,
    isAuthenticated: false,
    token: null,
    user: null,
    roles: {},
    allUsers: [],
    rememberMe: false,
    initialized: false,
    // Session management
    sessionExpiry: null,
    refreshAttempts: 0,
    isSessionExpired: false,
    // Password reset
    passwordResetLoading: false,
    passwordResetError: null,
    // Profile update
    profileUpdateLoading: false,
    profileUpdateError: null
  },
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
      state.status = "failed";
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setPasswordResetLoading: (state, action) => {
      state.passwordResetLoading = action.payload;
    },
    setPasswordResetError: (state, action) => {
      state.passwordResetError = action.payload;
    },
    setProfileUpdateLoading: (state, action) => {
      state.profileUpdateLoading = action.payload;
    },
    setProfileUpdateError: (state, action) => {
      state.profileUpdateError = action.payload;
    },
    setAuthData: (state, action) => {
      const { token, user, rememberMe = false } = action.payload;
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      state.rememberMe = rememberMe;
      state.initialized = true;
      state.error = null;
      state.status = "succeeded";
    },
    setUserData: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.initialized = true;
      state.error = null;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setAllUsers: (state, action) => {
      state.allUsers = action.payload;
    },
    addUser: (state, action) => {
      state.allUsers.push(action.payload);
    },
    updateUserInList: (state, action) => {
      const { userId, updates } = action.payload;
      const userIndex = state.allUsers.findIndex(user => 
        user._id === userId || user.id === userId
      );
      if (userIndex !== -1) {
        state.allUsers[userIndex] = { ...state.allUsers[userIndex], ...updates };
      }
    },
    removeUser: (state, action) => {
      const userId = action.payload;
      state.allUsers = state.allUsers.filter(user => 
        user._id !== userId && user.id !== userId
      );
    },
    setSessionExpiry: (state, action) => {
      state.sessionExpiry = action.payload;
    },
    setRefreshAttempts: (state, action) => {
      state.refreshAttempts = action.payload;
    },
    setSessionExpired: (state, action) => {
      state.isSessionExpired = action.payload;
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
    clearAuth: (state) => {
      state.status = "idle";
      state.error = null;
      state.loading = false;
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.roles = {};
      state.allUsers = [];
      state.rememberMe = false;
      state.initialized = true;
      state.sessionExpiry = null;
      state.refreshAttempts = 0;
      state.isSessionExpired = false;
      state.passwordResetLoading = false;
      state.passwordResetError = null;
      state.profileUpdateLoading = false;
      state.profileUpdateError = null;
    }
  },
});

export const {
  setError,
  setLoading,
  setPasswordResetLoading,
  setPasswordResetError,
  setProfileUpdateLoading,
  setProfileUpdateError,
  setAuthData,
  setUserData,
  setRoles,
  setAllUsers,
  addUser,
  updateUserInList,
  removeUser,
  setSessionExpiry,
  setRefreshAttempts,
  setSessionExpired,
  setInitialized,
  clearAuth
} = authSlice.actions;

// Selectors
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectToken = (state) => state.auth.token;
export const selectUser = (state) => state.auth.user;
export const selectRoles = (state) => state.auth.roles;
export const selectAllUsers = (state) => state.auth.allUsers;
export const selectRememberMe = (state) => state.auth.rememberMe;
export const selectInitialized = (state) => state.auth.initialized;
export const selectSessionExpiry = (state) => state.auth.sessionExpiry;
export const selectRefreshAttempts = (state) => state.auth.refreshAttempts;
export const selectIsSessionExpired = (state) => state.auth.isSessionExpired;
export const selectPasswordResetLoading = (state) => state.auth.passwordResetLoading;
export const selectPasswordResetError = (state) => state.auth.passwordResetError;
export const selectProfileUpdateLoading = (state) => state.auth.profileUpdateLoading;
export const selectProfileUpdateError = (state) => state.auth.profileUpdateError;

// Derived selectors
export const selectUserRole = (state) => state.auth.user?.role;

// Memoized selector to prevent unnecessary rerenders
export const selectUserPermissions = createSelector(
  [(state) => state.auth.user?.permissions],
  (permissions) => permissions || []
);
export const selectUserId = (state) => state.auth.user?.id || state.auth.user?._id;
export const selectUserClientId = (state) => state.auth.user?.client_id;
export const selectUserName = (state) => {
  const user = state.auth.user;
  if (!user) return 'Unknown User';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.username || user.email || 'Unknown User';
};
export const selectUserEmail = (state) => state.auth.user?.email;
export const selectUserDisplayName = (state) => {
  const user = state.auth.user;
  if (!user) return 'Unknown User';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.username || user.email || 'Unknown User';
};
export const selectUserInitials = (state) => {
  const user = state.auth.user;
  if (!user) return 'U';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  }
  return (user.username || user.email || 'U').charAt(0).toUpperCase();
};

// Role selectors
export const selectIsSuperAdmin = (state) => state.auth.user?.role === 'super_admin';
export const selectIsClientAdmin = (state) => state.auth.user?.role === 'client_admin';
export const selectIsClientHRManager = (state) => state.auth.user?.role === 'client_hr_manager';
export const selectIsClientInterviewer = (state) => state.auth.user?.role === 'client_interviewer';
export const selectIsClientViewer = (state) => state.auth.user?.role === 'client_viewer';

// Legacy role selectors for backward compatibility
export const selectIsAdmin = (state) => {
  const role = state.auth.user?.role;
  return role === 'admin' || role === 'super_admin';
};
export const selectIsHRManager = (state) => {
  const role = state.auth.user?.role;
  return role === 'hr_manager' || role === 'super_admin';
};
export const selectIsRecruiter = (state) => {
  const role = state.auth.user?.role;
  return role === 'recruiter' || role === 'super_admin';
};
export const selectIsViewer = (state) => {
  const role = state.auth.user?.role;
  return role === 'viewer' || role === 'super_admin';
};

export default authSlice.reducer; 